/**
 * Auth Context - Platform-admin authentication state management
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { PlatformAdminAuthUser } from '@resolve-ai/shared';
import { authApi, ApiError } from '../lib/api-client';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../lib/storage';

interface AuthState {
  admin: PlatformAdminAuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (token: string) => Promise<void>;
  exchangeOAuthCode: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token refresh interval (50 minutes - before 1 hour expiry)
const REFRESH_INTERVAL_MS = 50 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    admin: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const refreshAuth = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await authApi.refreshTokens(refreshToken);
      setTokens(response.accessToken, response.refreshToken);
      setState({ admin: response.admin, isLoading: false, isAuthenticated: true });
      return true;
    } catch {
      clearTokens();
      setState({ admin: null, isLoading: false, isAuthenticated: false });
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const accessToken = getAccessToken();
      if (!accessToken) {
        setState({ admin: null, isLoading: false, isAuthenticated: false });
        return;
      }

      try {
        const admin = await authApi.getCurrentAdmin();
        setState({ admin, isLoading: false, isAuthenticated: true });
      } catch (error) {
        if (error instanceof ApiError && error.code === 'auth_token_expired') {
          const refreshed = await refreshAuth();
          if (!refreshed) {
            clearTokens();
          }
        } else {
          clearTokens();
          setState({ admin: null, isLoading: false, isAuthenticated: false });
        }
      }
    };

    initAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      refreshAuth();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [state.isAuthenticated, refreshAuth]);

  const login = useCallback(async (token: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await authApi.verifyMagicLink(token);
      setTokens(response.accessToken, response.refreshToken);
      setState({ admin: response.admin, isLoading: false, isAuthenticated: true });
    } catch (error) {
      setState({ admin: null, isLoading: false, isAuthenticated: false });
      throw error;
    }
  }, []);

  const exchangeOAuthCode = useCallback(async (code: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await authApi.exchangeCode(code);
      setTokens(response.accessToken, response.refreshToken);
      setState({ admin: response.admin, isLoading: false, isAuthenticated: true });
    } catch (error) {
      setState({ admin: null, isLoading: false, isAuthenticated: false });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // Ignore logout errors - just clear local state
      }
    }

    clearTokens();
    setState({ admin: null, isLoading: false, isAuthenticated: false });
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    await authApi.sendMagicLink(email);
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, exchangeOAuthCode, logout, sendMagicLink }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
